import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Button, Alert, Spinner, Form, Modal, Badge, Table, ProgressBar, InputGroup } from 'react-bootstrap';
import {
  PhotoIcon,
  ArrowUpTrayIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CreditCardIcon,
  UserPlusIcon,
  UsersIcon,
  PencilSquareIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  Cog6ToothIcon,
  SparklesIcon,
  ShieldCheckIcon,
  ArrowTopRightOnSquareIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarDaysIcon,
  ClockIcon,
  GlobeAltIcon,
  BoltIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  LinkIcon,
  StarIcon,
  PlusCircleIcon,
  BellIcon,
} from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import { useCompanySettings } from './hooks/useCompanySettings';
import { authAPI, companyAPI } from '../../services/api';

/* ── Shared style tokens ── */
const sectionCard: React.CSSProperties = {
  borderRadius: '16px',
  border: '1px solid #e5e7eb',
  overflow: 'hidden',
  transition: 'box-shadow 0.2s ease',
};

const sectionHeader: React.CSSProperties = {
  background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 60%, #f0f9ff 100%)',
  padding: '20px 24px 16px',
  borderBottom: '1px solid #e5e7eb',
};

const sectionTitle: React.CSSProperties = {
  margin: 0,
  fontSize: '1rem',
  fontWeight: 700,
  color: '#1f2937',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const sectionSubtitle: React.CSSProperties = {
  margin: '4px 0 0',
  fontSize: '0.82rem',
  color: '#6b7280',
  lineHeight: 1.4,
};

const iconBubble = (bg: string): React.CSSProperties => ({
  width: '32px',
  height: '32px',
  borderRadius: '10px',
  background: bg,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const CompanySettings: React.FC = () => {
  const {
    isExpired, isOwner, canManageAdmins, canManagePlan,
    logoUrl, hasCustomBranding, loading, uploading, message, setMessage, previewUrl, selectedFile, fileInputRef, displayUrl,
    handleFileSelect, handleUpload, handleDelete,
    subStatus, subLoading, portalLoading, handleManageSubscription,
    isPaid, tierColor, tierLabel, trialPercentage, cancelAtPeriodEnd, cancelsAt,
    admins, maxAdmins, canAddAdmin, adminsLoading, showAdminModal, setShowAdminModal, editingAdmin, adminForm, setAdminForm,
    showAdminPassword, setShowAdminPassword, adminSaving, adminMessage, setAdminMessage,
    openAddAdminModal, openEditAdminModal, handleSaveAdmin, handleDeleteAdmin, handleToggleActive,
    leadDays, companyTimezone, leadDaysLoading, leadDaysSaving, leadDaysMessage, setLeadDaysMessage,
    handleSaveLeadDays,
    timezoneSaving, timezoneMessage, setTimezoneMessage, handleSaveTimezone,
  } = useCompanySettings();

  // Password change state
  const [pwOld, setPwOld] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwShowOld, setPwShowOld] = useState(false);
  const [pwShowNew, setPwShowNew] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMessage, setPwMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);

  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState({
    notify_order_updates_email: true,
    notify_order_updates_sms: false,
    notify_order_confirmations_email: true,
    notify_order_confirmations_sms: false,
    notify_promotions_email: false,
    notify_promotions_sms: false,
    notify_new_orders_email: true,
    notify_new_orders_sms: false,
    notify_payment_received_email: true,
    notify_payment_received_sms: false,
  });
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifMessage, setNotifMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);
  const [hasSmsPackage, setHasSmsPackage] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(false);

  useEffect(() => {
    const loadPrefs = async () => {
      try {
        const res = await authAPI.getNotificationPreferences();
        const { has_sms_package, sms_enabled, ...prefs } = res.data;
        setNotifPrefs(p => ({ ...p, ...prefs }));
        setHasSmsPackage(!!has_sms_package);
        setSmsEnabled(!!sms_enabled);
      } catch { /* use defaults */ }
      finally { setNotifLoading(false); }
    };
    loadPrefs();
  }, []);

  const handleToggleNotif = async (field: string, value: boolean) => {
    setNotifPrefs(p => ({ ...p, [field]: value }));
    setNotifMessage(null);
    setNotifSaving(true);
    try {
      await authAPI.updateNotificationPreferences({ [field]: value });
      setNotifMessage({ type: 'success', text: 'Preference updated.' });
    } catch {
      setNotifPrefs(p => ({ ...p, [field]: !value }));
      setNotifMessage({ type: 'danger', text: 'Failed to update preference.' });
    } finally { setNotifSaving(false); }
  };

  // Add-ons state
  interface AddOn { id: number; name: string; slug: string; description: string; service_type: string; service_type_display: string; price: string; is_recurring: boolean; icon: string; }
  interface PurchasedAddOn { id: number; addon_id: number; addon_name: string; addon_slug: string; addon_icon: string; status: string; purchased_at: string; expires_at: string | null; }

  const [addons, setAddons] = useState<AddOn[]>([]);
  const [purchasedAddons, setPurchasedAddons] = useState<PurchasedAddOn[]>([]);
  const [addonsLoading, setAddonsLoading] = useState(true);
  const [addonPurchasing, setAddonPurchasing] = useState<number | null>(null);
  const [addonMessage, setAddonMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);

  const addonIconMap: Record<string, React.ReactElement> = {
    bolt: <BoltIcon style={{ width: '20px', height: '20px', color: '#fff' }} />,
    chat: <ChatBubbleLeftRightIcon style={{ width: '20px', height: '20px', color: '#fff' }} />,
    chart: <ChartBarIcon style={{ width: '20px', height: '20px', color: '#fff' }} />,
    link: <LinkIcon style={{ width: '20px', height: '20px', color: '#fff' }} />,
    star: <StarIcon style={{ width: '20px', height: '20px', color: '#fff' }} />,
    envelope: <EnvelopeIcon style={{ width: '20px', height: '20px', color: '#fff' }} />,
    phone: <PhoneIcon style={{ width: '20px', height: '20px', color: '#fff' }} />,
  };

  useEffect(() => {
    const fetchAddons = async () => {
      try {
        const res = await companyAPI.getAddons();
        setAddons(res.data.available || []);
        setPurchasedAddons(res.data.purchased || []);
      } catch { /* silently fail */ }
      finally { setAddonsLoading(false); }
    };
    fetchAddons();
  }, []);

  const handlePurchaseAddon = async (addonId: number) => {
    setAddonPurchasing(addonId);
    setAddonMessage(null);
    try {
      const res = await companyAPI.purchaseAddon(addonId);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      setAddonMessage({ type: 'success', text: res.data.message || 'Add-on activated!' });
      // Refresh list
      const updated = await companyAPI.getAddons();
      setAddons(updated.data.available || []);
      setPurchasedAddons(updated.data.purchased || []);
    } catch (err: any) {
      setAddonMessage({ type: 'danger', text: err.response?.data?.error || 'Failed to purchase add-on.' });
    } finally { setAddonPurchasing(null); }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMessage(null);
    if (!pwOld || !pwNew || !pwConfirm) { setPwMessage({ type: 'danger', text: 'Please fill in all fields.' }); return; }
    if (pwNew.length < 8) { setPwMessage({ type: 'danger', text: 'New password must be at least 8 characters.' }); return; }
    if (pwNew !== pwConfirm) { setPwMessage({ type: 'danger', text: 'New passwords do not match.' }); return; }
    setPwSaving(true);
    try {
      await authAPI.changePassword(pwOld, pwNew);
      setPwMessage({ type: 'success', text: 'Password changed successfully!' });
      setPwOld(''); setPwNew(''); setPwConfirm('');
    } catch (err: any) {
      setPwMessage({ type: 'danger', text: err.response?.data?.error || 'Failed to change password.' });
    } finally { setPwSaving(false); }
  };

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding" style={{ maxWidth: '960px' }}>

        {/* ── Page Header ── */}
        <div className="d-flex align-items-center gap-2 mb-4">
          <div style={iconBubble('linear-gradient(135deg, #065f46, #059669)')}>
            <Cog6ToothIcon style={{ width: '18px', height: '18px', color: '#fff' }} />
          </div>
          <div>
            <h5 className="fw-bold mb-0" style={{ color: '#1f2937', fontSize: '1.2rem' }}>Settings</h5>
            <p className="mb-0" style={{ fontSize: '0.78rem', color: '#6b7280' }}>Manage your store, team, and subscription</p>
          </div>
        </div>

        {/* ── Store Logo ── */}
        {isOwner && (
        <Card className="mb-4 shadow-sm" style={sectionCard}>
          <div style={sectionHeader}>
            <div style={sectionTitle}>
              <SparklesIcon style={{ width: '18px', height: '18px', color: '#065f46' }} />
              Brand &amp; Logo
            </div>
            <p style={sectionSubtitle}>Customize how your store appears to customers</p>
          </div>
          <Card.Body className="p-4">
            {!hasCustomBranding && !loading && (
              <Alert variant="warning" className="d-flex align-items-start gap-2 py-2 px-3 mb-3" style={{ fontSize: '0.82rem', borderRadius: '10px', border: '1px solid #fde68a', background: '#fffbeb' }}>
                <LockClosedIcon style={{ width: '16px', height: '16px', flexShrink: 0, marginTop: '2px', color: '#92400e' }} />
                <div>
                  <strong>Custom branding locked.</strong>{' '}
                  <a href="/pricing" style={{ fontWeight: 600, color: '#92400e', textDecoration: 'underline' }}>Upgrade your plan</a> to upload a custom logo.
                </div>
              </Alert>
            )}

            {message && (
              <Alert
                variant={message.type}
                className="d-flex align-items-center gap-2 py-2 px-3 mb-3"
                style={{ fontSize: '0.85rem', borderRadius: '10px' }}
                dismissible
                onClose={() => setMessage(null)}
              >
                {message.type === 'success'
                  ? <CheckCircleIcon style={{ width: '16px', height: '16px' }} />
                  : <ExclamationCircleIcon style={{ width: '16px', height: '16px' }} />}
                {message.text}
              </Alert>
            )}

            {loading ? (
              <div className="text-center py-4"><Spinner animation="border" variant="success" /></div>
            ) : (
              <div className="d-flex flex-column flex-sm-row align-items-center gap-4">
                {/* Logo preview */}
                <div
                  style={{
                    width: '130px', height: '130px', borderRadius: '20px',
                    border: displayUrl ? '3px solid #065f46' : '3px dashed #d1d5db',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', flexShrink: 0,
                    backgroundColor: displayUrl ? '#f0fdf4' : '#f9fafb',
                    opacity: hasCustomBranding ? 1 : 0.45,
                    boxShadow: displayUrl ? '0 4px 14px rgba(6,95,70,0.12)' : 'none',
                  }}
                >
                  {displayUrl ? (
                    <img src={displayUrl} alt="Company Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div className="text-center" style={{ color: '#9ca3af' }}>
                      <PhotoIcon style={{ width: '36px', height: '36px', marginBottom: '2px' }} />
                      <div style={{ fontSize: '0.7rem' }}>No logo</div>
                    </div>
                  )}
                </div>

                {/* Right side — actions */}
                <div className="flex-grow-1 text-center text-sm-start">
                  {previewUrl && (
                    <div className="mb-2" style={{ fontSize: '0.8rem', color: '#065f46', fontWeight: 600 }}>
                      Preview — click "Save Logo" to apply
                    </div>
                  )}

                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                    onChange={handleFileSelect} style={{ display: 'none' }} aria-label="Upload company logo" />

                  {hasCustomBranding ? (
                    <>
                      <div className="d-flex gap-2 justify-content-center justify-content-sm-start flex-wrap">
                        <Button variant="outline-secondary" size="sm" className="d-flex align-items-center gap-1"
                          onClick={() => fileInputRef.current?.click()} disabled={uploading || isExpired}
                          style={{ borderRadius: '10px', fontWeight: 500, fontSize: '0.82rem' }}>
                          <ArrowUpTrayIcon style={{ width: '14px', height: '14px' }} />
                          {logoUrl ? 'Change' : 'Upload'}
                        </Button>
                        {previewUrl && selectedFile && (
                          <Button variant="success" size="sm" className="d-flex align-items-center gap-1"
                            onClick={handleUpload} disabled={uploading || isExpired}
                            style={{ borderRadius: '10px', fontWeight: 500, fontSize: '0.82rem', backgroundColor: '#065f46', borderColor: '#065f46' }}>
                            {uploading ? <Spinner animation="border" size="sm" /> : <CheckCircleIcon style={{ width: '14px', height: '14px' }} />}
                            Save Logo
                          </Button>
                        )}
                        {logoUrl && !previewUrl && (
                          <Button variant="outline-danger" size="sm" className="d-flex align-items-center gap-1"
                            onClick={handleDelete} disabled={uploading || isExpired}
                            style={{ borderRadius: '10px', fontWeight: 500, fontSize: '0.82rem' }}>
                            <TrashIcon style={{ width: '14px', height: '14px' }} />
                            Remove
                          </Button>
                        )}
                      </div>
                      <div className="mt-2" style={{ fontSize: '0.72rem', color: '#9ca3af' }}>
                        JPEG, PNG, GIF, WebP, SVG &bull; Max 2 MB &bull; Square recommended
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: '0.82rem', color: '#9ca3af' }}>
                      Upgrade to customize your store branding
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
        )}

        {/* ── Order Settings ── */}
        {isOwner && (
        <Card className="mb-4 shadow-sm" style={sectionCard}>
          <div style={sectionHeader}>
            <div style={sectionTitle}>
              <CalendarDaysIcon style={{ width: '18px', height: '18px', color: '#065f46' }} />
              Order Settings
            </div>
            <p style={sectionSubtitle}>Configure ordering rules for your customers</p>
          </div>
          <Card.Body className="p-4">
            {leadDaysLoading ? (
              <div className="text-center py-3"><Spinner animation="border" size="sm" variant="success" /></div>
            ) : (
              <>
                {leadDaysMessage && (
                  <Alert variant={leadDaysMessage.type} className="py-2 px-3 mb-3" style={{ fontSize: '0.82rem', borderRadius: '10px' }}
                    dismissible onClose={() => setLeadDaysMessage(null)}>
                    {leadDaysMessage.text}
                  </Alert>
                )}
                <div className="d-flex align-items-start gap-3">
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '12px',
                    background: 'rgba(37, 99, 235, 0.1)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <ClockIcon style={{ width: '20px', height: '20px', color: '#2563eb' }} />
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-semibold mb-1" style={{ color: '#1f2937', fontSize: '0.9rem' }}>Minimum Delivery Lead Time</div>
                    <p className="mb-2" style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                      How many days in advance customers must order. For example, if set to 2, a customer ordering today can select delivery from {(() => {
                        const d = new Date(); d.setDate(d.getDate() + leadDays);
                        return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                      })()} at the earliest.
                    </p>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {[0, 1, 2, 3, 5, 7].map(d => (
                        <Button
                          key={d}
                          variant={leadDays === d ? 'success' : 'outline-secondary'}
                          size="sm"
                          disabled={leadDaysSaving || isExpired}
                          onClick={() => handleSaveLeadDays(d)}
                          style={{
                            borderRadius: '10px', fontWeight: 600, fontSize: '0.8rem',
                            minWidth: '72px',
                            ...(leadDays === d ? { backgroundColor: '#065f46', borderColor: '#065f46' } : {}),
                          }}
                        >
                          {d === 0 ? 'Same day' : d === 1 ? '1 day' : `${d} days`}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Timezone Picker */}
                <hr className="my-4" style={{ borderColor: '#e5e7eb' }} />
                {timezoneMessage && (
                  <Alert variant={timezoneMessage.type} className="py-2 px-3 mb-3" style={{ fontSize: '0.82rem', borderRadius: '10px' }}
                    dismissible onClose={() => setTimezoneMessage(null)}>
                    {timezoneMessage.text}
                  </Alert>
                )}
                <div className="d-flex align-items-start gap-3">
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '12px',
                    background: 'rgba(37, 99, 235, 0.1)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <GlobeAltIcon style={{ width: '20px', height: '20px', color: '#2563eb' }} />
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-semibold mb-1" style={{ color: '#1f2937', fontSize: '0.9rem' }}>Company Timezone</div>
                    <p className="mb-2" style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                      Used to calculate "Tomorrow's Bake" and delivery dates. Make sure this matches your bakery's local timezone.
                    </p>
                    <Form.Select
                      size="sm"
                      value={companyTimezone}
                      onChange={(e) => handleSaveTimezone(e.target.value)}
                      disabled={timezoneSaving || isExpired}
                      style={{ maxWidth: '320px', fontSize: '0.85rem', borderRadius: '10px' }}
                    >
                      <optgroup label="US Timezones">
                        <option value="America/New_York">Eastern (New York)</option>
                        <option value="America/Chicago">Central (Chicago)</option>
                        <option value="America/Denver">Mountain (Denver)</option>
                        <option value="America/Los_Angeles">Pacific (Los Angeles)</option>
                        <option value="America/Anchorage">Alaska (Anchorage)</option>
                        <option value="Pacific/Honolulu">Hawaii (Honolulu)</option>
                      </optgroup>
                      <optgroup label="Americas">
                        <option value="America/Toronto">Toronto</option>
                        <option value="America/Vancouver">Vancouver</option>
                        <option value="America/Mexico_City">Mexico City</option>
                        <option value="America/Sao_Paulo">São Paulo</option>
                        <option value="America/Argentina/Buenos_Aires">Buenos Aires</option>
                        <option value="America/Bogota">Bogotá</option>
                      </optgroup>
                      <optgroup label="Europe">
                        <option value="Europe/London">London</option>
                        <option value="Europe/Paris">Paris</option>
                        <option value="Europe/Berlin">Berlin</option>
                        <option value="Europe/Istanbul">Istanbul</option>
                        <option value="Europe/Moscow">Moscow</option>
                      </optgroup>
                      <optgroup label="Asia & Pacific">
                        <option value="Asia/Dubai">Dubai</option>
                        <option value="Asia/Kolkata">India (Kolkata)</option>
                        <option value="Asia/Shanghai">China (Shanghai)</option>
                        <option value="Asia/Tokyo">Tokyo</option>
                        <option value="Asia/Seoul">Seoul</option>
                        <option value="Australia/Sydney">Sydney</option>
                        <option value="Pacific/Auckland">Auckland</option>
                      </optgroup>
                    </Form.Select>
                  </div>
                </div>
              </>
            )}
          </Card.Body>
        </Card>
        )}

        {/* ── Team Admins ── */}
        {canManageAdmins && (
          <Card className="mb-4 shadow-sm" style={sectionCard}>
            <div style={sectionHeader}>
              <div className="d-flex justify-content-between align-items-center">
                <div style={sectionTitle}>
                  <UsersIcon style={{ width: '18px', height: '18px', color: '#065f46' }} />
                  Team Admins
                </div>
                <Button
                  variant="success" size="sm"
                  className="d-flex align-items-center gap-1"
                  onClick={openAddAdminModal}
                  disabled={!canAddAdmin || isExpired}
                  style={{ borderRadius: '10px', fontWeight: 600, fontSize: '0.78rem', backgroundColor: '#065f46', borderColor: '#065f46', whiteSpace: 'nowrap', padding: '6px 14px' }}
                >
                  <UserPlusIcon style={{ width: '14px', height: '14px' }} />
                  Add Staff
                </Button>
              </div>
              <p style={sectionSubtitle}>
                Staff members with access to selected sections.
                <span style={{ color: '#065f46', fontWeight: 700, marginLeft: '6px' }}>
                  {admins.length}/{Math.max(0, maxAdmins - 1)} team slots
                </span>
              </p>
              {/* Slot usage bar */}
              <div className="mt-2" style={{ maxWidth: '200px' }}>
                <ProgressBar
                  now={maxAdmins > 1 ? (admins.length / (maxAdmins - 1)) * 100 : 0}
                  style={{ height: '6px', borderRadius: '4px', backgroundColor: '#e5e7eb' }}
                  variant={admins.length >= maxAdmins - 1 && maxAdmins > 1 ? 'warning' : 'success'}
                />
              </div>
            </div>
            <Card.Body className="p-3 p-md-4">
              {!canAddAdmin && maxAdmins <= 1 && (
                <Alert variant="info" className="py-2 px-3 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '0.82rem', borderRadius: '10px', border: '1px solid #bfdbfe', background: '#eff6ff' }}>
                  <ShieldCheckIcon style={{ width: '16px', height: '16px', color: '#2563eb', flexShrink: 0 }} />
                  <div>
                    Your plan supports {maxAdmins} admin.{' '}
                    <a href="/pricing" style={{ fontWeight: 600, color: '#1d4ed8' }}>Upgrade</a> to invite team members.
                  </div>
                </Alert>
              )}

              {adminsLoading ? (
                <div className="text-center py-4"><Spinner animation="border" size="sm" variant="success" /></div>
              ) : admins.length === 0 ? (
                <div className="text-center py-4">
                  <UsersIcon style={{ width: '36px', height: '36px', color: '#d1d5db', marginBottom: '8px' }} />
                  <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>No team admins yet. You are the only admin.</div>
                </div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="d-none d-md-block table-responsive">
                    <Table hover size="sm" className="mb-0 align-middle" style={{ fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ color: '#64748b', borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ fontWeight: 600, padding: '10px 12px' }}>Name</th>
                          <th style={{ fontWeight: 600, padding: '10px 12px' }}>Email</th>
                          <th style={{ fontWeight: 600, padding: '10px 12px' }}>Plan Access</th>
                          <th style={{ fontWeight: 600, padding: '10px 12px' }}>Status</th>
                          <th style={{ fontWeight: 600, padding: '10px 12px' }} className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {admins.map(admin => (
                          <tr key={admin.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td className="fw-medium" style={{ padding: '12px' }}>{admin.first_name} {admin.last_name}</td>
                            <td style={{ color: '#64748b', padding: '12px' }}>{admin.email}</td>
                            <td style={{ padding: '12px' }}>
                              {(() => {
                                const sectionKeys = ['access_production','access_batch_optimizer','access_inventory','access_purchase_orders','access_starters','access_store_settings','access_store_customers','access_store_orders'] as const;
                                const count = sectionKeys.filter(k => admin.permissions?.[k as keyof typeof admin.permissions]).length;
                                return (
                                  <Badge bg="none" style={{ fontSize: '0.7rem', fontWeight: 600, background: count > 0 ? '#dbeafe' : '#f3f4f6', color: count > 0 ? '#1e40af' : '#6b7280', padding: '4px 10px', borderRadius: '20px' }}>
                                    {count > 0 ? `${count} section${count > 1 ? 's' : ''}` : 'No Access'}
                                  </Badge>
                                );
                              })()}
                            </td>
                            <td style={{ padding: '12px' }}>
                              <Badge
                                bg="none"
                                style={{
                                  cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600,
                                  background: admin.is_active ? '#d1fae5' : '#f3f4f6',
                                  color: admin.is_active ? '#065f46' : '#6b7280',
                                  padding: '4px 10px', borderRadius: '20px',
                                }}
                                onClick={() => handleToggleActive(admin)}
                              >
                                {admin.is_active ? 'Active' : 'Disabled'}
                              </Badge>
                            </td>
                            <td className="text-end" style={{ padding: '12px' }}>
                              <Button variant="link" size="sm" className="p-1 me-1" onClick={() => openEditAdminModal(admin)}
                                style={{ color: '#065f46', borderRadius: '8px' }}>
                                <PencilSquareIcon style={{ width: '16px', height: '16px' }} />
                              </Button>
                              <Button variant="link" size="sm" className="p-1" onClick={() => handleDeleteAdmin(admin)}
                                style={{ color: '#dc2626', borderRadius: '8px' }}>
                                <TrashIcon style={{ width: '16px', height: '16px' }} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* Mobile cards */}
                  <div className="d-md-none d-flex flex-column gap-2">
                    {admins.map(admin => (
                      <div key={admin.id} style={{
                        background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px',
                        padding: '14px 16px',
                      }}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1f2937' }}>
                              {admin.first_name} {admin.last_name}
                            </div>
                            <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>@{admin.username}</div>
                          </div>
                          <Badge
                            bg="none"
                            style={{
                              cursor: 'pointer', fontSize: '0.68rem', fontWeight: 600,
                              background: admin.is_active ? '#d1fae5' : '#f3f4f6',
                              color: admin.is_active ? '#065f46' : '#6b7280',
                              padding: '4px 10px', borderRadius: '20px',
                            }}
                            onClick={() => handleToggleActive(admin)}
                          >
                            {admin.is_active ? 'Active' : 'Disabled'}
                          </Badge>
                        </div>
                        {admin.email && (
                          <div className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                            <EnvelopeIcon style={{ width: '12px', height: '12px' }} /> {admin.email}
                          </div>
                        )}
                        <div className="d-flex align-items-center gap-1 mt-1">
                          {(() => {
                            const sectionKeys = [
                              'access_customers','access_products','access_orders',
                              'access_ingredients','access_suppliers','access_inventory','access_purchase_orders',
                              'access_recipes','access_starters','access_production',
                              'access_batch_optimizer','access_cost_calculator','access_baker_calculator',
                              'access_store_settings','access_store_customers','access_store_orders','access_messages',
                            ];
                            const count = sectionKeys.filter(k => admin.permissions?.[k as keyof typeof admin.permissions]).length;
                            return (
                              <Badge bg="none" style={{
                                fontSize: '0.68rem', fontWeight: 600,
                                background: count > 0 ? '#dbeafe' : '#f3f4f6',
                                color: count > 0 ? '#1e40af' : '#6b7280',
                                padding: '3px 8px', borderRadius: '20px',
                              }}>
                                {count > 0 ? `${count} section${count !== 1 ? 's' : ''}` : 'No section access'}
                              </Badge>
                            );
                          })()}
                        </div>
                        <div className="d-flex gap-2 mt-2 pt-2" style={{ borderTop: '1px solid #f3f4f6' }}>
                          <Button variant="outline-secondary" size="sm" className="d-flex align-items-center gap-1"
                            onClick={() => openEditAdminModal(admin)}
                            style={{ borderRadius: '8px', fontSize: '0.75rem', padding: '3px 10px' }}>
                            <PencilSquareIcon style={{ width: '13px', height: '13px' }} /> Edit
                          </Button>
                          <Button variant="outline-danger" size="sm" className="d-flex align-items-center gap-1"
                            onClick={() => handleDeleteAdmin(admin)}
                            style={{ borderRadius: '8px', fontSize: '0.75rem', padding: '3px 10px' }}>
                            <TrashIcon style={{ width: '13px', height: '13px' }} /> Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        )}

        {/* ── Subscription ── */}
        {canManagePlan && (
        <Card className="mb-4 shadow-sm" style={sectionCard}>
          <div style={sectionHeader}>
            <div style={sectionTitle}>
              <CreditCardIcon style={{ width: '18px', height: '18px', color: '#065f46' }} />
              Subscription
            </div>
            <p style={sectionSubtitle}>Manage your plan, billing, and invoices</p>
          </div>
          <Card.Body className="p-4">
            {subLoading ? (
              <div className="text-center py-4"><Spinner animation="border" size="sm" variant="success" /></div>
            ) : subStatus ? (
              <>
                {/* Plan info card */}
                <div style={{
                  background: isPaid
                    ? 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)'
                    : 'linear-gradient(135deg, #fffbeb 0%, #fefce8 100%)',
                  border: `1px solid ${isPaid ? '#a7f3d0' : '#fde68a'}`,
                  borderRadius: '14px', padding: '18px 20px',
                }}>
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-3">
                      <div style={iconBubble(isPaid ? '#059669' : '#d97706')}>
                        {isPaid
                          ? <ShieldCheckIcon style={{ width: '18px', height: '18px', color: '#fff' }} />
                          : <SparklesIcon style={{ width: '18px', height: '18px', color: '#fff' }} />}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, color: '#1f2937', fontSize: '1.05rem' }}>
                          {subStatus.subscription_plan?.name || (isPaid ? 'Paid Plan' : 'Free Trial')}
                        </div>
                        {subStatus.subscription_plan?.price && (
                          <div style={{ color: '#6b7280', fontSize: '0.82rem' }}>
                            ${subStatus.subscription_plan.price}/month
                          </div>
                        )}
                      </div>
                    </div>
                    <span style={{
                      background: tierColor, color: '#fff',
                      padding: '5px 14px', borderRadius: '20px',
                      fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' as const,
                      letterSpacing: '0.5px',
                    }}>
                      {tierLabel}
                    </span>
                  </div>

                  {/* Trial progress */}
                  {trialPercentage !== null && (
                    <div className="mt-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span style={{ fontSize: '0.78rem', color: '#92400e', fontWeight: 600 }}>
                          {subStatus.days_remaining > 0 ? `${subStatus.days_remaining} days remaining` : 'Trial expired'}
                        </span>
                        <span style={{ fontSize: '0.72rem', color: '#b45309' }}>14-day trial</span>
                      </div>
                      <ProgressBar
                        now={trialPercentage}
                        style={{ height: '6px', borderRadius: '4px', backgroundColor: '#fef3c7' }}
                        variant={subStatus.days_remaining <= 3 ? 'danger' : 'warning'}
                      />
                    </div>
                  )}

                  {isPaid && !cancelAtPeriodEnd && subStatus.next_renewal_date && (
                    <div className="mt-2" style={{ color: '#059669', fontSize: '0.8rem' }}>
                      Next renewal: {new Date(subStatus.next_renewal_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </div>
                  )}

                  {cancelAtPeriodEnd && (
                    <div className="mt-3" style={{
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)',
                      border: '1px solid #f59e0b',
                      borderRadius: '10px',
                      padding: '12px 16px',
                    }}>
                      <div style={{ fontWeight: 700, color: '#92400e', fontSize: '0.85rem', marginBottom: '2px' }}>
                        Subscription Cancelled
                      </div>
                      <div style={{ color: '#78350f', fontSize: '0.8rem' }}>
                        Your subscription has been cancelled but remains active until{' '}
                        <strong>
                          {cancelsAt
                            ? cancelsAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                            : subStatus.next_renewal_date
                              ? new Date(subStatus.next_renewal_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                              : 'the end of your billing period'}
                        </strong>.
                        You can resubscribe anytime before then to keep your plan.
                      </div>
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div className="d-flex gap-2 flex-wrap mt-3">
                  {subStatus.has_active_subscription ? (
                    <Button variant="outline-success" size="sm" className="d-flex align-items-center gap-2"
                      onClick={handleManageSubscription} disabled={portalLoading}
                      style={{ borderRadius: '10px', fontWeight: 600, fontSize: '0.82rem', padding: '8px 18px' }}>
                      {portalLoading ? <Spinner animation="border" size="sm" /> : <ArrowTopRightOnSquareIcon style={{ width: '14px', height: '14px' }} />}
                      Manage Billing
                    </Button>
                  ) : (
                    <Button variant="success" size="sm" className="d-flex align-items-center gap-2"
                      onClick={() => window.location.href = '/pricing'}
                      style={{ borderRadius: '10px', fontWeight: 600, fontSize: '0.82rem', padding: '8px 18px', backgroundColor: '#065f46', borderColor: '#065f46' }}>
                      <CreditCardIcon style={{ width: '14px', height: '14px' }} />
                      Upgrade Plan
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <div className="text-muted text-center py-3" style={{ fontSize: '0.85rem' }}>
                Unable to load subscription information.
              </div>
            )}
          </Card.Body>
        </Card>
        )}

        {/* ── Add-On Services ── */}
        {isOwner && !addonsLoading && addons.length > 0 && (
        <Card style={sectionCard} className="mb-4">
          <div style={sectionHeader}>
            <h6 style={sectionTitle}>
              <div style={iconBubble('linear-gradient(135deg, #7c3aed, #a855f7)')}>
                <PlusCircleIcon style={{ width: '15px', height: '15px', color: '#fff' }} />
              </div>
              Add-On Services
            </h6>
            <p style={sectionSubtitle}>Enhance your plan with additional services</p>
          </div>
          <Card.Body style={{ padding: '24px' }}>
            {addonMessage && (
              <Alert variant={addonMessage.type} className="py-2 px-3 mb-3" style={{ fontSize: '0.84rem', borderRadius: '10px' }}
                dismissible onClose={() => setAddonMessage(null)}>
                {addonMessage.text}
              </Alert>
            )}
            <Row className="g-3">
              {addons.map(addon => {
                const purchased = purchasedAddons.find(p => p.addon_id === addon.id);
                const isActive = purchased?.status === 'active';
                return (
                  <Col xs={12} md={6} key={addon.id}>
                    <div style={{
                      border: isActive ? '2px solid #059669' : '1px solid #e5e7eb',
                      borderRadius: '14px',
                      padding: '20px',
                      background: isActive ? '#f0fdf4' : '#fff',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s ease',
                    }}>
                      <div className="d-flex align-items-start gap-3 mb-3">
                        <div style={{
                          ...iconBubble(isActive ? 'linear-gradient(135deg, #059669, #10b981)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)'),
                          width: '40px', height: '40px', borderRadius: '12px',
                        }}>
                          {addonIconMap[addon.icon] || <BoltIcon style={{ width: '20px', height: '20px', color: '#fff' }} />}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937' }}>{addon.name}</span>
                            {isActive && (
                              <Badge bg="" style={{ background: '#059669', fontSize: '0.65rem', padding: '3px 8px', borderRadius: '20px' }}>
                                Active
                              </Badge>
                            )}
                          </div>
                          <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0, lineHeight: 1.4 }}>{addon.description}</p>
                        </div>
                      </div>
                      <div className="mt-auto d-flex align-items-center justify-content-between">
                        <div>
                          <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1f2937' }}>${addon.price}</span>
                          <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginLeft: '2px' }}>
                            {addon.is_recurring ? '/mo' : ' one-time'}
                          </span>
                        </div>
                        {isActive ? (
                          <div className="d-flex align-items-center gap-1" style={{ color: '#059669', fontSize: '0.82rem', fontWeight: 600 }}>
                            <CheckCircleIcon style={{ width: '16px', height: '16px' }} />
                            Active
                          </div>
                        ) : (
                          <Button size="sm" disabled={addonPurchasing === addon.id}
                            onClick={() => handlePurchaseAddon(addon.id)}
                            style={{
                              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                              border: 'none', borderRadius: '10px', padding: '6px 18px',
                              fontWeight: 600, fontSize: '0.82rem',
                            }}>
                            {addonPurchasing === addon.id ? <Spinner size="sm" /> : 'Get Started'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Card.Body>
        </Card>
        )}

        {/* ── Change Password ── */}
        <Card style={sectionCard} className="mb-4">
          <div style={sectionHeader}>
            <h6 style={sectionTitle}>
              <div style={iconBubble('#065f46')}>
                <LockClosedIcon style={{ width: '15px', height: '15px', color: '#fff' }} />
              </div>
              Change Password
            </h6>
            <p style={sectionSubtitle}>Update your account password</p>
          </div>
          <Card.Body style={{ padding: '24px' }}>
            {pwMessage && (
              <Alert variant={pwMessage.type} className="py-2 px-3 mb-3" style={{ fontSize: '0.84rem', borderRadius: '10px' }}
                dismissible onClose={() => setPwMessage(null)}>
                {pwMessage.text}
              </Alert>
            )}
            <Form onSubmit={handleChangePassword}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem', color: '#374151' }}>Current Password</Form.Label>
                <div className="position-relative">
                  <Form.Control type={pwShowOld ? 'text' : 'password'} value={pwOld}
                    onChange={e => setPwOld(e.target.value)} placeholder="Enter current password"
                    style={{ borderRadius: '10px', padding: '10px 40px 10px 14px' }} />
                  <button type="button" className="position-absolute"
                    style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    onClick={() => setPwShowOld(!pwShowOld)}>
                    {pwShowOld ? <EyeSlashIcon style={{ width: '16px', height: '16px', color: '#9ca3af' }} /> : <EyeIcon style={{ width: '16px', height: '16px', color: '#9ca3af' }} />}
                  </button>
                </div>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem', color: '#374151' }}>New Password</Form.Label>
                <div className="position-relative">
                  <Form.Control type={pwShowNew ? 'text' : 'password'} value={pwNew}
                    onChange={e => setPwNew(e.target.value)} placeholder="Enter new password (min 8 characters)"
                    style={{ borderRadius: '10px', padding: '10px 40px 10px 14px' }} />
                  <button type="button" className="position-absolute"
                    style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    onClick={() => setPwShowNew(!pwShowNew)}>
                    {pwShowNew ? <EyeSlashIcon style={{ width: '16px', height: '16px', color: '#9ca3af' }} /> : <EyeIcon style={{ width: '16px', height: '16px', color: '#9ca3af' }} />}
                  </button>
                </div>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem', color: '#374151' }}>Confirm New Password</Form.Label>
                <Form.Control type="password" value={pwConfirm}
                  onChange={e => setPwConfirm(e.target.value)} placeholder="Re-enter new password"
                  style={{ borderRadius: '10px', padding: '10px 14px' }} />
              </Form.Group>
              <Button type="submit" disabled={pwSaving}
                style={{ background: 'linear-gradient(135deg, #065f46, #059669)', border: 'none', borderRadius: '10px', padding: '10px 28px', fontWeight: 600, fontSize: '0.88rem' }}>
                {pwSaving ? <><Spinner size="sm" className="me-2" />Saving...</> : 'Update Password'}
              </Button>
            </Form>
          </Card.Body>
        </Card>

        {/* ── Communication Preferences ── */}
        {isOwner && (
        <Card style={sectionCard} className="mb-4">
          <div style={sectionHeader}>
            <h6 style={sectionTitle}>
              <div style={iconBubble('#065f46')}>
                <BellIcon style={{ width: '15px', height: '15px', color: '#fff' }} />
              </div>
              Communication Preferences
            </h6>
            <p style={sectionSubtitle}>Choose which notifications you'd like to receive</p>
          </div>
          <Card.Body style={{ padding: '24px' }}>
            {notifMessage && (
              <Alert variant={notifMessage.type} className="py-2 px-3 mb-3" style={{ fontSize: '0.84rem', borderRadius: '10px' }}
                dismissible onClose={() => setNotifMessage(null)}>
                {notifMessage.type === 'success' && <CheckCircleIcon style={{ width: '16px', height: '16px', display: 'inline', marginRight: '4px' }} />}
                {notifMessage.text}
              </Alert>
            )}
            {notifLoading ? (
              <div className="text-center py-3"><Spinner size="sm" /></div>
            ) : (
              <div>
                {([
                  { emailKey: 'notify_new_orders_email' as const, smsKey: 'notify_new_orders_sms' as const, label: 'New Order Alerts', desc: 'Get notified when a customer places a new order' },
                  { emailKey: 'notify_payment_received_email' as const, smsKey: 'notify_payment_received_sms' as const, label: 'Payment Received', desc: 'Get notified when a payment is recorded' },
                  { emailKey: 'notify_order_confirmations_email' as const, smsKey: 'notify_order_confirmations_sms' as const, label: 'Order Confirmations', desc: 'Receive order confirmation notifications' },
                  { emailKey: 'notify_order_updates_email' as const, smsKey: 'notify_order_updates_sms' as const, label: 'Order Status Updates', desc: 'Get notified when order status changes' },
                  { emailKey: 'notify_promotions_email' as const, smsKey: 'notify_promotions_sms' as const, label: 'Promotions & News', desc: 'Receive promotional offers and platform updates' },
                ]).map(({ emailKey, smsKey, label, desc }) => (
                  <div key={emailKey} className="py-3" style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1f2937', marginBottom: '2px' }}>{label}</div>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '10px' }}>{desc}</div>
                    <div className="d-flex align-items-center gap-4">
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '0.78rem', color: '#374151', fontWeight: 500, minWidth: '65px' }}>📧 Email</span>
                        <Form.Check type="switch" id={`notif-${emailKey}`}
                          checked={notifPrefs[emailKey]} onChange={e => handleToggleNotif(emailKey, e.target.checked)}
                          disabled={notifSaving} style={{ transform: 'scale(1.1)' }} />
                      </div>
                      {smsEnabled && (
                      <div className="d-flex align-items-center gap-2" title={!hasSmsPackage ? 'SMS package required — purchase in Add-On Services above' : ''}>
                        <span style={{ fontSize: '0.78rem', color: hasSmsPackage ? '#374151' : '#9ca3af', fontWeight: 500, minWidth: '75px' }}>💬 Text/SMS</span>
                        <Form.Check type="switch" id={`notif-${smsKey}`}
                          checked={notifPrefs[smsKey]} onChange={e => handleToggleNotif(smsKey, e.target.checked)}
                          disabled={notifSaving || !hasSmsPackage} style={{ transform: 'scale(1.1)' }} />
                      </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
        )}
      </Container>

      {/* ── Add/Edit Admin Modal ── */}
      <Modal show={showAdminModal} onHide={() => setShowAdminModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e7eb', padding: '16px 24px' }}>
          <Modal.Title style={{ fontSize: '1.05rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={iconBubble('#065f46')}>
              {editingAdmin
                ? <PencilSquareIcon style={{ width: '15px', height: '15px', color: '#fff' }} />
                : <UserPlusIcon style={{ width: '15px', height: '15px', color: '#fff' }} />}
            </div>
            {editingAdmin ? 'Edit Staff Member' : 'Add Staff Member'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px 24px' }}>
          {adminMessage && (
            <Alert variant={adminMessage.type} className="py-2 px-3 mb-3" style={{ fontSize: '0.82rem', borderRadius: '10px' }}
              dismissible onClose={() => setAdminMessage(null)}>
              {adminMessage.text}
            </Alert>
          )}
          <Row>
            <Col xs={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem', color: '#374151' }}>First Name</Form.Label>
                <Form.Control size="sm" value={adminForm.first_name}
                  onChange={e => setAdminForm(f => ({ ...f, first_name: e.target.value }))}
                  placeholder="John" style={{ borderRadius: '10px', padding: '8px 12px' }} />
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem', color: '#374151' }}>Last Name</Form.Label>
                <Form.Control size="sm" value={adminForm.last_name}
                  onChange={e => setAdminForm(f => ({ ...f, last_name: e.target.value }))}
                  placeholder="Doe" style={{ borderRadius: '10px', padding: '8px 12px' }} />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem', color: '#374151' }}>
              Username <span style={{ color: '#dc2626' }}>*</span>
            </Form.Label>
            <Form.Control size="sm" value={adminForm.username}
              onChange={e => setAdminForm(f => ({ ...f, username: e.target.value }))}
              placeholder="johndoe" disabled={!!editingAdmin}
              style={{ borderRadius: '10px', padding: '8px 12px' }} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem', color: '#374151' }}>
              Email <span style={{ color: '#dc2626' }}>*</span>
            </Form.Label>
            <div className="position-relative">
              <Form.Control size="sm" type="email" value={adminForm.email}
                onChange={e => setAdminForm(f => ({ ...f, email: e.target.value }))}
                placeholder="john@example.com"
                style={{ borderRadius: '10px', padding: '8px 12px' }} />
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem', color: '#374151' }}>
              <PhoneIcon style={{ width: '13px', height: '13px', marginRight: '4px', color: '#6b7280' }} />
              Phone
            </Form.Label>
            <Form.Control size="sm" value={adminForm.phone_number}
              onChange={e => setAdminForm(f => ({ ...f, phone_number: e.target.value }))}
              placeholder="555-1234"
              style={{ borderRadius: '10px', padding: '8px 12px' }} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem', color: '#374151' }}>
              <LockClosedIcon style={{ width: '13px', height: '13px', marginRight: '4px', color: '#6b7280' }} />
              Password {editingAdmin ? <span style={{ fontWeight: 400, color: '#9ca3af' }}>(leave blank to keep)</span> : <span style={{ color: '#dc2626' }}>*</span>}
            </Form.Label>
            <InputGroup size="sm">
              <Form.Control type={showAdminPassword ? 'text' : 'password'} value={adminForm.password}
                onChange={e => setAdminForm(f => ({ ...f, password: e.target.value }))}
                placeholder={editingAdmin ? '••••••' : 'Enter password'}
                style={{ borderRadius: '10px 0 0 10px', padding: '8px 12px' }} />
              <Button variant="outline-secondary" onClick={() => setShowAdminPassword(!showAdminPassword)}
                style={{ borderRadius: '0 10px 10px 0', borderLeft: 'none', background: 'white' }}>
                {showAdminPassword ? <EyeSlashIcon style={{ width: '14px', height: '14px', color: '#64748b' }} /> : <EyeIcon style={{ width: '14px', height: '14px', color: '#64748b' }} />}
              </Button>
            </InputGroup>
          </Form.Group>

          {/* ── Permissions ── */}
          <div className="mb-2 mt-3">
            <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem', color: '#374151', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ShieldCheckIcon style={{ width: '13px', height: '13px', color: '#6b7280' }} />
              Section Access
            </Form.Label>
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '12px 16px' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Main Navigation</div>
              {([
                { key: 'access_customers' as const, label: 'Customers', desc: 'View and manage wholesale customers' },
                { key: 'access_products' as const, label: 'Products', desc: 'View and manage product catalog' },
                { key: 'access_orders' as const, label: 'Orders', desc: 'View and manage orders' },
              ]).map(({ key, label, desc }) => (
                <Form.Check key={key} type="checkbox" id={`perm-${key}`} className="mb-2"
                  label={<span style={{ fontSize: '0.82rem' }}><span style={{ fontWeight: 600, color: '#1f2937' }}>{label}</span><span style={{ color: '#6b7280', marginLeft: '6px' }}>&mdash; {desc}</span></span>}
                  checked={adminForm.permissions?.[key] ?? false}
                  onChange={e => setAdminForm(f => ({ ...f, permissions: { ...f.permissions, [key]: e.target.checked } }))}
                  style={{ accentColor: '#065f46' }} />
              ))}
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '12px', marginBottom: '10px' }}>Kitchen & Production</div>
              {([
                { key: 'access_production' as const, label: 'Production', desc: 'View and manage production runs' },
                { key: 'access_batch_optimizer' as const, label: 'Batch Optimizer', desc: 'Run batch optimization plans' },
                { key: 'access_starters' as const, label: 'Starters', desc: 'Manage sourdough starters' },
                { key: 'access_recipes' as const, label: 'Recipes', desc: 'View and manage recipes' },
              ]).map(({ key, label, desc }) => (
                <Form.Check key={key} type="checkbox" id={`perm-${key}`} className="mb-2"
                  label={<span style={{ fontSize: '0.82rem' }}><span style={{ fontWeight: 600, color: '#1f2937' }}>{label}</span><span style={{ color: '#6b7280', marginLeft: '6px' }}>&mdash; {desc}</span></span>}
                  checked={adminForm.permissions?.[key] ?? false}
                  onChange={e => setAdminForm(f => ({ ...f, permissions: { ...f.permissions, [key]: e.target.checked } }))}
                  style={{ accentColor: '#065f46' }} />
              ))}
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '12px', marginBottom: '10px' }}>Warehouse</div>
              {([
                { key: 'access_inventory' as const, label: 'Inventory Management', desc: 'View and adjust stock levels' },
                { key: 'access_purchase_orders' as const, label: 'Purchase Orders', desc: 'Create and receive purchase orders' },
                { key: 'access_ingredients' as const, label: 'Ingredients', desc: 'View and manage ingredients' },
                { key: 'access_suppliers' as const, label: 'Suppliers', desc: 'View and manage suppliers' },
              ]).map(({ key, label, desc }) => (
                <Form.Check key={key} type="checkbox" id={`perm-${key}`} className="mb-2"
                  label={<span style={{ fontSize: '0.82rem' }}><span style={{ fontWeight: 600, color: '#1f2937' }}>{label}</span><span style={{ color: '#6b7280', marginLeft: '6px' }}>&mdash; {desc}</span></span>}
                  checked={adminForm.permissions?.[key] ?? false}
                  onChange={e => setAdminForm(f => ({ ...f, permissions: { ...f.permissions, [key]: e.target.checked } }))}
                  style={{ accentColor: '#065f46' }} />
              ))}
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '12px', marginBottom: '10px' }}>Webstore</div>
              {([
                { key: 'access_store_settings' as const, label: 'Store Settings', desc: 'Manage webstore configuration' },
                { key: 'access_store_customers' as const, label: 'Store Customers', desc: 'View and manage store customers' },
                { key: 'access_store_orders' as const, label: 'Store Orders', desc: 'View and manage store orders' },
                { key: 'access_messages' as const, label: 'Messages', desc: 'View and respond to customer messages' },
              ]).map(({ key, label, desc }) => (
                <Form.Check key={key} type="checkbox" id={`perm-${key}`} className="mb-2"
                  label={<span style={{ fontSize: '0.82rem' }}><span style={{ fontWeight: 600, color: '#1f2937' }}>{label}</span><span style={{ color: '#6b7280', marginLeft: '6px' }}>&mdash; {desc}</span></span>}
                  checked={adminForm.permissions?.[key] ?? false}
                  onChange={e => setAdminForm(f => ({ ...f, permissions: { ...f.permissions, [key]: e.target.checked } }))}
                  style={{ accentColor: '#065f46' }} />
              ))}
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '12px', marginBottom: '10px' }}>Tools</div>
              {([
                { key: 'access_cost_calculator' as const, label: 'Cost Calculator', desc: 'Use the cost calculation tool' },
                { key: 'access_baker_calculator' as const, label: "Baker's Calculator", desc: "Use the baker's percentage calculator" },
                { key: 'access_support' as const, label: 'Support', desc: 'Access the support section' },
              ]).map(({ key, label, desc }) => (
                <Form.Check key={key} type="checkbox" id={`perm-${key}`} className="mb-2"
                  label={<span style={{ fontSize: '0.82rem' }}><span style={{ fontWeight: 600, color: '#1f2937' }}>{label}</span><span style={{ color: '#6b7280', marginLeft: '6px' }}>&mdash; {desc}</span></span>}
                  checked={adminForm.permissions?.[key] ?? false}
                  onChange={e => setAdminForm(f => ({ ...f, permissions: { ...f.permissions, [key]: e.target.checked } }))}
                  style={{ accentColor: '#065f46' }} />
              ))}
              <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '12px', paddingTop: '12px' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Admin</div>
                {([
                  { key: 'can_view_financials' as const, label: 'Financials', desc: 'View financial reports & dashboard' },
                  { key: 'can_manage_plan' as const, label: 'Plan Management', desc: 'Manage subscription & billing' },
                  { key: 'can_manage_admins' as const, label: 'Manage Team', desc: 'Add, edit, and remove staff members' },
                ]).map(({ key, label, desc }) => (
                  <Form.Check key={key} type="checkbox" id={`perm-${key}`} className="mb-2"
                    label={<span style={{ fontSize: '0.82rem' }}><span style={{ fontWeight: 600, color: '#1f2937' }}>{label}</span><span style={{ color: '#6b7280', marginLeft: '6px' }}>&mdash; {desc}</span></span>}
                    checked={adminForm.permissions?.[key] ?? false}
                    onChange={e => setAdminForm(f => ({ ...f, permissions: { ...f.permissions, [key]: e.target.checked } }))}
                    style={{ accentColor: '#065f46' }} />
                ))}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '12px 24px' }}>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowAdminModal(false)}
            style={{ borderRadius: '10px', padding: '6px 18px', fontWeight: 500 }}>Cancel</Button>
          <Button variant="success" size="sm" onClick={handleSaveAdmin} disabled={adminSaving || !adminForm.username || !adminForm.email}
            style={{ borderRadius: '10px', padding: '6px 18px', backgroundColor: '#065f46', borderColor: '#065f46', fontWeight: 600 }}>
            {adminSaving ? <Spinner animation="border" size="sm" /> : (editingAdmin ? 'Save Changes' : 'Add Staff')}
          </Button>
        </Modal.Footer>
      </Modal>
    </CompanyLayout>
  );
};

export default CompanySettings;
